import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminApi } from '../../utils/adminApi';
import { adminListUsers } from '../../utils/cognitoAuthService';

const STALE_MS = 30_000;

const isStale = (lastFetched) => !lastFetched || Date.now() - lastFetched > STALE_MS;

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const fetchUsers = createAsyncThunk(
  'userManagement/fetchUsers',
  async (force = false, { getState }) => {
    const { usersLastFetched } = getState().userManagement;
    if (!force && !isStale(usersLastFetched)) return null;
    const result = await adminListUsers();
    if (!result.success) throw new Error('Failed to fetch users');
    return result.users || [];
  }
);

export const fetchGroups = createAsyncThunk(
  'userManagement/fetchGroups',
  async (force = false, { getState }) => {
    const { groupsLastFetched } = getState().userManagement;
    if (!force && !isStale(groupsLastFetched)) return null;
    const res = await adminApi.get('/user-groups');
    return res.data.groups || [];
  }
);

export const fetchGlobalIP = createAsyncThunk(
  'userManagement/fetchGlobalIP',
  async (force = false, { getState }) => {
    const { globalIPLastFetched } = getState().userManagement;
    if (!force && !isStale(globalIPLastFetched)) return null;
    const res = await adminApi.get('/site-settings/globalIPRestrictions/config');
    return res.data?.settingValue || { enabled: false, allowedIPs: [] };
  }
);

export const fetchGroupPermissions = createAsyncThunk(
  'userManagement/fetchGroupPermissions',
  async (groupId, { getState }) => {
    const cached = getState().userManagement.groupPermissionsCache[groupId];
    if (cached) return null; // already in cache
    const res = await adminApi.get(`/user-groups/${groupId}/permissions`);
    return { groupId, permissions: res.data.permissions };
  }
);

export const fetchUserPermissions = createAsyncThunk(
  'userManagement/fetchUserPermissions',
  async (username, { getState }) => {
    const cached = getState().userManagement.userPermissionsCache[username];
    if (cached) return null; // already in cache
    const res = await adminApi.get(`/user-permissions/${username}`);
    return {
      username,
      hasOverrides: res.data.hasOverrides,
      permissions: res.data.permissions,
    };
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState: {
    users: [],
    usersStatus: 'idle',
    usersLastFetched: null,

    groups: [],
    groupsStatus: 'idle',
    groupsLastFetched: null,

    globalIPConfig: { enabled: false, allowedIPs: [] },
    globalIPStatus: 'idle',
    globalIPLastFetched: null,

    groupPermissionsCache: {},  // { [groupId]: permissions }
    userPermissionsCache: {},   // { [username]: { hasOverrides, permissions } }
  },

  reducers: {
    optimisticUpdateUser(state, { payload: { username, changes } }) {
      const idx = state.users.findIndex((u) => u.username === username);
      if (idx !== -1) state.users[idx] = { ...state.users[idx], ...changes };
    },
    removeUser(state, { payload: username }) {
      state.users = state.users.filter((u) => u.username !== username);
    },
    cacheGroupPermissions(state, { payload: { groupId, permissions } }) {
      state.groupPermissionsCache[groupId] = permissions;
    },
    markGroupHasCustomPermissions(state, { payload: { groupId, hasCustom } }) {
      const g = state.groups.find((g) => String(g.id) === String(groupId));
      if (g) g.hasCustomPermissions = hasCustom;
    },
    cacheUserPermissions(state, { payload: { username, hasOverrides, permissions } }) {
      state.userPermissionsCache[username] = { hasOverrides, permissions };
    },
    clearUserPermissionsCache(state, { payload: username }) {
      delete state.userPermissionsCache[username];
    },
    invalidateUsers(state) {
      state.usersLastFetched = null;
    },
    invalidateGroups(state) {
      state.groupsLastFetched = null;
    },
  },

  extraReducers: (builder) => {
    // Users
    builder
      .addCase(fetchUsers.pending, (state) => { state.usersStatus = 'loading'; })
      .addCase(fetchUsers.fulfilled, (state, { payload }) => {
        state.usersStatus = 'succeeded';
        if (payload !== null) {
          state.users = payload;
          state.usersLastFetched = Date.now();
        }
      })
      .addCase(fetchUsers.rejected, (state) => { state.usersStatus = 'failed'; });

    // Groups
    builder
      .addCase(fetchGroups.pending, (state) => { state.groupsStatus = 'loading'; })
      .addCase(fetchGroups.fulfilled, (state, { payload }) => {
        state.groupsStatus = 'succeeded';
        if (payload !== null) {
          state.groups = payload;
          state.groupsLastFetched = Date.now();
        }
      })
      .addCase(fetchGroups.rejected, (state) => { state.groupsStatus = 'failed'; });

    // Global IP
    builder
      .addCase(fetchGlobalIP.pending, (state) => { state.globalIPStatus = 'loading'; })
      .addCase(fetchGlobalIP.fulfilled, (state, { payload }) => {
        state.globalIPStatus = 'succeeded';
        if (payload !== null) {
          state.globalIPConfig = payload;
          state.globalIPLastFetched = Date.now();
        }
      })
      .addCase(fetchGlobalIP.rejected, (state) => { state.globalIPStatus = 'failed'; });

    // Group permissions cache
    builder.addCase(fetchGroupPermissions.fulfilled, (state, { payload }) => {
      if (payload) {
        state.groupPermissionsCache[payload.groupId] = payload.permissions;
      }
    });

    // User permissions cache
    builder.addCase(fetchUserPermissions.fulfilled, (state, { payload }) => {
      if (payload) {
        state.userPermissionsCache[payload.username] = {
          hasOverrides: payload.hasOverrides,
          permissions: payload.permissions,
        };
      }
    });
  },
});

export const {
  optimisticUpdateUser,
  removeUser,
  cacheGroupPermissions,
  markGroupHasCustomPermissions,
  cacheUserPermissions,
  clearUserPermissionsCache,
  invalidateUsers,
  invalidateGroups,
} = userManagementSlice.actions;

export default userManagementSlice.reducer;
